import { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { toElement } from "../../lib/maplibre-gl-map-to-image";
import { PDFDocument, rgb, PageSizes } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import {
  PDF_DOWNLOAD_FILE_NAME,
  PDF_SIZE_MM,
  PDF_SIZE_PX,
  PT_PER_MM,
  PX_PER_PDF_PT,
  DPI_OPTIONS,
} from "../../constants/Constant";
import { loadState, saveState } from "../../utils/SaveStateUtil";
import { useAuthStatus } from "../../context/AuthContext";
import { isIOSSafari } from "../../utils/Util";

/**
 * PDF作成用のモーダルコンポーネント
 * @param {object} props
 * @param {boolean} props.show モーダルを表示するかどうかの状態
 * @param {function} props.handleClose モーダルを閉じる関数
 * @param {object} props.map MapLibre GL JSの地図インスタンス
 * @param {boolean} props.isCheckSaveState 状態を保存するかどうかのフラグ
 */
const PdfModal = ({ show, handleClose, map, isCheckSaveState }) => {
  const imageRef = useRef(null);
  const [isPdfCreating, setIsPdfCreating] = useState(false);
  const [showAnnotation, setShowAnnotation] = useState(false);
  const [confirmDialogShow, setConfirmDialogShow] = useState(false);
  const pdfUrlRef = useRef(null);
  const isEnabledSaveStateRef = useRef(false);
  const { isLoading, isUserLoggedIn } = useAuthStatus();
  const isFirstLoadRef = useRef(true);

  // 設定エリア用の状態管理
  const [pageSize, setPageSize] = useState("A4");
  const [orientation, setOrientation] = useState("vertical");
  const [marginTop, setMarginTop] = useState("10.00");
  const [marginBottom, setMarginBottom] = useState("10.00");
  const [marginLeft, setMarginLeft] = useState("10.00");
  const [marginRight, setMarginRight] = useState("10.00");
  const [title, setTitle] = useState("");
  const [dpi, setDpi] = useState(96);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    // 初期表示の状態読込
    if (isFirstLoadRef.current) {
      if (isUserLoggedIn) {
        const checkMapState = loadState("checkMapState");
        const pdfSetting = loadState("pdfSetting");
        // 初回は、保存データから再現ONか読む（以降はpropから取得）
        if (checkMapState?.isCheckSaveState && pdfSetting) {
          setPageSize(pdfSetting.pageSize);
          setOrientation(pdfSetting.orientation);
          setMarginTop(pdfSetting.marginTop);
          setMarginBottom(pdfSetting.marginBottom);
          setMarginLeft(pdfSetting.marginLeft);
          setMarginRight(pdfSetting.marginRight);
          setTitle(pdfSetting.title);
          setDpi(pdfSetting.dpi);
        }
      }
      isFirstLoadRef.current = false;
    }
    // 設定保存可否
    isEnabledSaveStateRef.current = isUserLoggedIn && isCheckSaveState;
  }, [isCheckSaveState, isLoading, isUserLoggedIn]);

  // 設定保存
  useEffect(() => {
    const debounce = setTimeout(() => {
      if (isEnabledSaveStateRef.current) {
        saveState("pdfSetting", {
          pageSize,
          orientation,
          marginTop,
          marginBottom,
          marginLeft,
          marginRight,
          title,
          dpi,
        });
      }
    }, 500); // デバウンスで遅延させる
    return () => {
      clearTimeout(debounce);
    };
  }, [
    isCheckSaveState,
    pageSize,
    orientation,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    title,
    dpi,
  ]);

  // 傾きの状態チェック
  useEffect(() => {
    setShowAnnotation(show && map && map.getPitch() !== 0);
  }, [map, show]);

  /**
   * 地図の画像を作成する
   */
  const createMapImage = async (margin, textHeignt) => {
    if (!map) return;

    // 作成する画像サイズの計算
    const calcImageSize = () => {
      const marginXPx =
        (margin.top + margin.bottom + textHeignt) * PX_PER_PDF_PT;
      const marginYPx = (margin.left + margin.right) * PX_PER_PDF_PT;

      return orientation === "vertical"
        ? [
            PDF_SIZE_PX[pageSize][0] - marginXPx,
            PDF_SIZE_PX[pageSize][1] - marginYPx,
          ]
        : [
            PDF_SIZE_PX[pageSize][1] - marginXPx,
            PDF_SIZE_PX[pageSize][0] - marginYPx,
          ];
    };

    // toElementを呼び出して地図画像を更新
    await toElement(map, {
      targetImageId: "pdfMapImage",
      coverEdits: true,
      format: "png",
      hideControlsInCorner: ["top-right"],
      hideMarkers: true,
      size: calcImageSize(),
      dpi,
    });
  };

  /**
   * 画像URLまたはBase64データURLからArrayBufferを取得するユーティリティ関数
   * @param {string} url 画像のデータURLまたは通常のURL
   */
  const fetchDataBytes = async (url) => {
    // Base64データURLの場合は直接デコード
    if (url.startsWith("data:")) {
      const base64Data = url.split(",")[1];
      const decodedData = atob(base64Data);
      const uint8Array = new Uint8Array(decodedData.length);
      for (let i = 0; i < decodedData.length; ++i) {
        uint8Array[i] = decodedData.charCodeAt(i);
      }
      return uint8Array.buffer;
    }

    // 通常のURLの場合はfetchで取得
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Failed to fetch image from ${url}`);
    }
    return res.arrayBuffer();
  };

  /**
   * PDFを生成する非同期関数
   * @param {boolean} isDownload ダウンロードするか、プレビューするかを制御
   */
  const generatePdf = async (isDownload = true) => {
    // 作成中制御
    setIsPdfCreating(true);

    // PDFドキュメントの作成
    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);

    // ページのサイズと向きを動的に設定
    const pageDimensions = PageSizes[pageSize.toUpperCase()];
    const page = pdfDoc.addPage(
      orientation === "vertical"
        ? pageDimensions
        : [pageDimensions[1], pageDimensions[0]]
    );
    const { width, height } = page.getSize();
    const margin = {
      // mmをptに変換
      top: parseFloat(marginTop) * PT_PER_MM,
      bottom: parseFloat(marginBottom) * PT_PER_MM,
      left: parseFloat(marginLeft) * PT_PER_MM,
      right: parseFloat(marginRight) * PT_PER_MM,
    };

    // 日本語フォントの埋め込み (IPAexGothic)
    const fontUrl = "/font/ipaexg.ttf";
    let customFont;
    try {
      const fontBytes = await fetchDataBytes(fontUrl);
      customFont = await pdfDoc.embedFont(fontBytes, { subset: true });
    } catch (error) {
      console.error(
        "日本語フォントの取得または埋め込みに失敗しました。",
        error
      );
      alert("フォントファイルが見つからないため、PDFを生成できません。");
      setIsPdfCreating(false);
      return;
    }

    // タイトルの描画（中央寄せ）
    const titleSize = 24;
    let titleY = height - margin.top;
    if (title) {
      const titleWidth = customFont.widthOfTextAtSize(title, titleSize);
      const titleX = (width - titleWidth) / 2;
      titleY -= titleSize;
      page.drawText(title, {
        x: titleX,
        y: titleY,
        font: customFont,
        size: titleSize,
        color: rgb(0, 0, 0),
      });
    }

    // 地図画像の生成
    await createMapImage(margin, title ? titleSize + 20 : 0);

    // 画像の埋め込み (<img>タグからデータを取得)
    const imageElement = imageRef.current;
    if (imageElement && imageElement.src) {
      try {
        const imgBytes = await fetchDataBytes(imageElement.src);

        let image;
        const imageMimeType = imageElement.src.startsWith("data:")
          ? imageElement.src.split(";")[0].split(":")[1]
          : "image/png";

        if (imageMimeType === "image/png") {
          image = await pdfDoc.embedPng(imgBytes);
        } else if (imageMimeType === "image/jpeg") {
          image = await pdfDoc.embedJpg(imgBytes);
        } else {
          console.error("サポートされていない画像形式です。");
          alert("サポートされていない画像形式のため、PDFを生成できません。");
          setIsPdfCreating(false);
          return;
        }

        const scaleFactor = dpi / 72; // 72 points per inch
        page.drawImage(image, {
          x: margin.left,
          y: margin.bottom,
          width: image.width / scaleFactor,
          height: image.height / scaleFactor,
        });
      } catch (error) {
        console.error("画像データの取得または埋め込みに失敗しました。", error);
        alert("画像データの取得に失敗しました。");
        setIsPdfCreating(false);
        return;
      }
    } else {
      console.error("画像データが見つかりません。");
    }

    // PDFを保存
    const outputPdfBytes = await pdfDoc.save();

    // ダウンロードまたはプレビュー
    let blob;
    if (isIOSSafari()) {
      blob = new Blob([outputPdfBytes], {
        type: isDownload ? "application/octet-stream" : "application/pdf",
      });
    } else {
      blob = new Blob([outputPdfBytes], { type: "application/pdf" });
    }
    const url = URL.createObjectURL(blob);
    if (isDownload) {
      const link = document.createElement("a");
      link.href = url;
      link.download = PDF_DOWNLOAD_FILE_NAME; // ダウンロードファイル名
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      pdfUrlRef.current = url;
      setConfirmDialogShow(true);
    }

    // 作成終了
    setIsPdfCreating(false);
    imageElement.src = null;
  };

  // マージンの入力値を制限する
  const validateAndSetMargin = () => {
    const maxMm =
      orientation === "vertical"
        ? PDF_SIZE_MM[pageSize]
        : PDF_SIZE_MM[pageSize].toReversed();
    const marginArray = [
      [marginTop, setMarginTop, maxMm[0]],
      [marginBottom, setMarginBottom, maxMm[0]],
      [marginLeft, setMarginLeft, maxMm[1]],
      [marginRight, setMarginRight, maxMm[1]],
    ];
    marginArray.forEach(([value, setStateFunc, max]) => {
      let returnValue = parseFloat(value);
      if (isNaN(returnValue) || returnValue < 0) {
        returnValue = 0;
      } else if (returnValue > max) {
        returnValue = max;
      }
      setStateFunc(parseFloat(returnValue).toFixed(2)); // 小数点以下2桁
    });
  };
  useEffect(() => {
    // サイズ、向きが変わった時に実行
    validateAndSetMargin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, orientation]);

  // safari用のプレビュー表示
  const handlePreviewPdfOnSafari = () => {
    setConfirmDialogShow(false);
    window.open(pdfUrlRef.current);
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static">
        <Modal.Header closeButton={isPdfCreating ? false : true}>
          <Modal.Title>PDFの作成</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* 設定エリア */}
          <div className="mb-2 mx-4" style={{ position: "relative" }}>
            <h5>PDF設定</h5>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>サイズ</Form.Label>
                <div className="d-flex gap-3">
                  {Object.keys(PDF_SIZE_MM).map((key) => (
                    <Form.Check
                      key={key}
                      type="radio"
                      id={`pageSize-${key}`}
                      name="pageSize"
                      label={key}
                      value={key}
                      checked={pageSize === key}
                      onChange={() => setPageSize(key)}
                    />
                  ))}
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>向き</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    id="orientation-vertical"
                    name="orientation"
                    label="縦"
                    value="vertical"
                    checked={orientation === "vertical"}
                    onChange={(e) => setOrientation(e.target.value)}
                  />
                  <Form.Check
                    type="radio"
                    id="orientation-horizontal"
                    name="orientation"
                    label="横"
                    value="horizontal"
                    checked={orientation === "horizontal"}
                    onChange={(e) => setOrientation(e.target.value)}
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="mb-0">余白 (mm)</Form.Label>
                <Row className="gap-3 mb-1">
                  <Col xs="auto" className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0">上</Form.Label>
                    <Form.Control
                      type="number"
                      step={0.01}
                      min={0}
                      max={300}
                      style={{ width: "100px" }}
                      value={marginTop}
                      onChange={(e) => setMarginTop(e.target.value)}
                      onBlur={validateAndSetMargin}
                    />
                  </Col>
                  <Col xs="auto" className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0">下</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={300}
                      style={{ width: "100px" }}
                      value={marginBottom}
                      onChange={(e) => setMarginBottom(e.target.value)}
                      onBlur={validateAndSetMargin}
                    />
                  </Col>
                </Row>
                <Row className="gap-3">
                  <Col xs="auto" className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0">左</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={300}
                      style={{ width: "100px" }}
                      value={marginLeft}
                      onChange={(e) => setMarginLeft(e.target.value)}
                      onBlur={validateAndSetMargin}
                    />
                  </Col>
                  <Col xs="auto" className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0">右</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={300}
                      style={{ width: "100px" }}
                      value={marginRight}
                      onChange={(e) => setMarginRight(e.target.value)}
                      onBlur={validateAndSetMargin}
                    />
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>タイトル</Form.Label>
                <Form.Control
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={50}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>DPI</Form.Label>
                <div className="d-flex gap-3">
                  {DPI_OPTIONS.map((value) => (
                    <Form.Check
                      key={value}
                      type="radio"
                      id={`dpi-${value}`}
                      name="dpi"
                      label={value}
                      value={value}
                      checked={dpi === value}
                      onChange={() => setDpi(value)}
                    />
                  ))}
                </div>
              </Form.Group>
              {showAnnotation ? (
                <div className="mt-3">※ 地図の傾きや範囲が変わる可能性があります</div>
              ) : null}
            </Form>
            {/* 地図画像 */}
            <img
              ref={imageRef}
              id="pdfMapImage"
              alt=""
              style={{
                display: "none",
              }}
            />
            {/* スピナー */}
            {isPdfCreating ? (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <Spinner animation="border" role="status" />
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" onClick={handleClose} disabled={isPdfCreating}>
            閉じる
          </Button>
          <div className="flex-fill" />
          <Button
            variant="outline-info"
            onClick={() => generatePdf(false)}
            disabled={isPdfCreating}
          >
            プレビュー
          </Button>
          <Button
            variant="primary"
            onClick={() => generatePdf(true)}
            disabled={isPdfCreating}
          >
            PDFダウンロード
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        size="sm"
        show={confirmDialogShow}
        onHide={() => {
          setConfirmDialogShow(false);
        }}
        centered
        backdrop="static"
        style={{
          width: "80vw",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translateY(-50%) translateX(-50%)",
        }}
      >
        <Modal.Header>表示確認</Modal.Header>
        <Modal.Body style={{ height: "80px" }}>
          PDFのプレビューを表示します。
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            onClick={() => {
              setConfirmDialogShow(false);
            }}
          >
            キャンセル
          </Button>
          <div className="flex-fill" />
          <Button variant="primary" onClick={handlePreviewPdfOnSafari}>
            表示する
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PdfModal;
